import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminConfigSyncApi } from '../../services/api';
import DevOnlyGuard from '../../components/common/DevOnlyGuard';
import {
  SlidersHorizontal, Play, Loader, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronRight, RotateCcw, AlertTriangle, Ban, Info, ShieldAlert
} from 'lucide-react';

/**
 * Admin Config Sync — reconciles AppCfg_* configuration between the preprod and prod
 * ADMIN databases (DevPlan step 86).
 *
 * SAFETY, per Danny 2026-08-06:
 *   - Every row starts UNCHECKED. Clicking straight through applies nothing.
 *   - An acknowledgement checkbox naming the target database must be ticked before Apply
 *     enables. (A typed-database-name modal sat behind this initially; Danny cut it as too
 *     much friction on top of a checkbox that already names the target.)
 *   - The acknowledgement resets on apply, on direction change, and on any selection change,
 *     so it can never carry from the rows the operator read to a different set.
 *   - Neither environment is the "safe" one. Prod and preprod get identical friction.
 *   - Rows whose foreign keys have no counterpart in the target are not selectable, and
 *     privilege-bearing fields are flagged — the review is the control.
 */
const AdminConfigSyncPage = () => {
  const [availability, setAvailability] = useState(null);
  const [direction, setDirection] = useState('preprod-to-prod');

  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState(null);

  // Selection is a Set of "table::key" tokens. Empty by default and never auto-populated.
  const [selected, setSelected] = useState(new Set());
  const [expandedTables, setExpandedTables] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  // Acknowledgement gate. A typed-database-name modal used to sit behind this; Danny cut it
  // as too much friction on top of a checkbox that already names the target database.
  const [acknowledged, setAcknowledged] = useState(false);

  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const isProdTarget = diff?.targetEnvironment === 'prod';

  // ── Gate reset ─────────────────────────────────────────────────────
  // Anything that changes WHAT would be written invalidates the acknowledgement, so it can
  // never carry over from the set of rows the operator actually read to a different one.
  const resetGates = useCallback(() => {
    setAcknowledged(false);
  }, []);

  // ── Data loading ───────────────────────────────────────────────────
  useEffect(() => {
    adminConfigSyncApi.getAvailability()
      .then(res => setAvailability(res.data))
      .catch(() => setAvailability({
        available: false,
        reason: 'Unable to reach the sync service.',
        environments: [], phases: []
      }));
  }, []);

  const loadHistory = useCallback(() => {
    adminConfigSyncApi.getHistory()
      .then(res => setHistory(res.data || []))
      .catch(() => setHistory([]));
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadDiff = useCallback(async (dir) => {
    setDiffLoading(true);
    setDiffError(null);
    setDiff(null);
    setSelected(new Set());
    setApplyResult(null);
    resetGates();
    try {
      const res = await adminConfigSyncApi.getDiff(dir);
      setDiff(res.data);
      // Open tables that actually have something to look at.
      const open = {};
      (res.data.tables || []).forEach(t => {
        if (t.rows?.some(r => r.verdict !== 'TARGET_ONLY')) open[t.table] = true;
      });
      setExpandedTables(open);
    } catch (err) {
      setDiffError(err.response?.data?.error || err.message);
    }
    setDiffLoading(false);
  }, [resetGates]);

  useEffect(() => {
    if (availability?.available) loadDiff(direction);
  }, [direction, availability, loadDiff]);

  // ── Selection ──────────────────────────────────────────────────────
  const tokenOf = (table, key) => `${table}::${key}`;

  const toggleRow = (table, key) => {
    setSelected(prev => {
      const next = new Set(prev);
      const t = tokenOf(table, key);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
    resetGates();
  };

  const setTableSelection = (t, on) => {
    setSelected(prev => {
      const next = new Set(prev);
      t.rows.filter(isSelectable)
        .forEach(r => on ? next.add(tokenOf(t.table, r.key)) : next.delete(tokenOf(t.table, r.key)));
      return next;
    });
    resetGates();
  };

  // A row is selectable only if it is actionable AND applicable. blockedReason means a
  // foreign key has no counterpart in the target, so there is nothing valid to write.
  const isSelectable = (r) => r.verdict !== 'TARGET_ONLY' && !r.blockedReason;

  const selectedRows = useMemo(() => {
    if (!diff) return [];
    const out = [];
    diff.tables.forEach(t => {
      if (t.blockedReason) return;          // whole table barred in this direction
      t.rows.forEach(r => {
        if (isSelectable(r) && selected.has(tokenOf(t.table, r.key)))
          out.push({ table: t.table, key: r.key, sourceFingerprint: r.sourceFingerprint });
      });
    });
    return out;
  }, [diff, selected]);

  const perTableCounts = useMemo(() => {
    const m = {};
    selectedRows.forEach(r => { m[r.table] = (m[r.table] || 0) + 1; });
    return m;
  }, [selectedRows]);

  // ── Apply ──────────────────────────────────────────────────────────
  const runApply = async () => {
    if (!diff || selectedRows.length === 0 || !acknowledged || applying) return;

    setApplying(true);
    try {
      // The server still verifies this against the catalog it resolved for the direction.
      // Auto-filled rather than typed, but it remains a real coherence check: if the client's
      // idea of the target has drifted from the server's, the apply is refused.
      const res = await adminConfigSyncApi.apply(direction, selectedRows, diff.targetDatabase);
      setApplyResult(res.data);
      loadHistory();
      // Re-diff so the page reflects reality rather than the pre-apply picture.
      const fresh = await adminConfigSyncApi.getDiff(direction);
      setDiff(fresh.data);
    } catch (err) {
      setApplyResult(err.response?.data?.status
        ? err.response.data
        : { status: 'FAILED', errorMessage: err.response?.data?.error || err.message, results: [] });
    }
    setSelected(new Set());
    resetGates();
    setApplying(false);
  };

  // ── Helpers ────────────────────────────────────────────────────────
  const fmtDate = (s) => {
    if (!s) return '--';
    const d = new Date(s);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const canApply = selectedRows.length > 0 && acknowledged && !applying;

  const tablesWithWork = (diff?.tables || []).filter(t => !t.blockedReason && t.rows.length > 0);
  const blockedTables = (diff?.tables || []).filter(t => t.blockedReason);
  const totalApplicable = diff?.tables?.reduce(
    (n, t) => n + (t.blockedReason ? 0 : t.rows.filter(isSelectable).length), 0) || 0;
  const totalBlockedRows = diff?.tables?.reduce(
    (n, t) => n + (t.blockedReason ? 0 : t.rows.filter(r => r.blockedReason).length), 0) || 0;

  // ── Render ─────────────────────────────────────────────────────────
  // Block the whole section, not just the panels. Same treatment as the other dev-only pages.
  if (availability && !availability.available) {
    return <DevOnlyGuard pageName="Admin Config Sync" message={availability.reason} />;
  }

  return (
    <div className="dev-module sync-dashboard-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <SlidersHorizontal size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Admin Config Sync
          </h1>
          {diff && (
            <span className={`dev-module-meta ${isProdTarget ? 'schema-env-prod' : ''}`}>
              {diff.sourceDatabase} &rarr; {diff.targetDatabase}
            </span>
          )}
        </div>
      </div>

      <div className="dev-module-content">
        <div className="sync-steps-layout">

          {/* ═══════════ 1: SETUP ═══════════ */}
          <div className="stui-step-panel sync-setup-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>Setup</h3>
            </div>
            <p className="stui-step-desc">
              Compares <strong>AppCfg_*</strong> configuration between the two admin databases.
              Only the target is written.
            </p>

            <label className="schema-field-label" htmlFor="acs-direction">Direction</label>
            <select
              id="acs-direction"
              className={`sync-db-select ${isProdTarget ? 'is-prod' : ''}`}
              value={direction}
              onChange={e => setDirection(e.target.value)}
              disabled={applying || diffLoading}
            >
              <option value="preprod-to-prod">PreProd &rarr; Prod</option>
              <option value="prod-to-preprod">Prod &rarr; PreProd</option>
            </select>

            {diff && (
              <div className={isProdTarget ? 'schema-prod-warning' : 'acs-target-note'}>
                <AlertTriangle size={13} />
                <span>
                  Writes <strong>{diff.targetDatabase}</strong>. Reads{' '}
                  <strong>{diff.sourceDatabase}</strong> and never modifies it.
                </span>
              </div>
            )}

            <div className="acs-policy-note">
              <Info size={13} />
              <span>
                This tool never deletes. Rows that exist only in the target are listed for
                information and are not actionable.
              </span>
            </div>

            <div className="sync-history-section">
              <button className="sync-history-toggle" onClick={() => setHistoryOpen(!historyOpen)}>
                {historyOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <Clock size={14} />
                History ({history.length})
              </button>
              {historyOpen && history.length > 0 && (
                <div className="sync-history-table-wrap">
                  <table className="sync-history-table">
                    <thead>
                      <tr>
                        <th>Date</th><th>Direction</th>
                        <th className="center">Status</th><th className="right">Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice(0, 10).map((h, i) => (
                        <tr key={i}>
                          <td>{fmtDate(h.started_At)}</td>
                          <td>{h.project}</td>
                          <td className="center">
                            <span className={`sync-status-badge ${h.status?.toLowerCase()}`}>{h.status}</span>
                          </td>
                          <td className="right">{h.objects_Applied ?? '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {historyOpen && history.length === 0 && (
                <p className="sync-no-history">No admin config syncs have been run.</p>
              )}
            </div>
          </div>

          {/* ═══════════ 2: REVIEW ═══════════ */}
          <div className="stui-step-panel sync-execute-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">2</span>
              <h3>Review</h3>
            </div>

            {diffLoading && (
              <div className="sync-running-status">
                <div className="sync-running-header">
                  <Loader size={18} className="spinning" />
                  <span>Comparing configuration...</span>
                </div>
              </div>
            )}

            {diffError && (
              <div className="sync-complete-brief failed">
                <XCircle size={18} className="icon-fail" />
                <div>
                  <div className="sync-complete-label">Could not build the diff</div>
                  <div className="sync-complete-stats">{diffError}</div>
                </div>
              </div>
            )}

            {diff?.fatalError && (
              <div className="sync-complete-brief failed">
                <ShieldAlert size={18} className="icon-fail" />
                <div>
                  <div className="sync-complete-label">Diff refused</div>
                  <div className="sync-complete-stats">{diff.fatalError}</div>
                </div>
              </div>
            )}

            {diff && !diff.fatalError && totalApplicable === 0 && (
              <div className="sync-complete-brief success">
                <CheckCircle size={18} className="icon-success" />
                <div>
                  <div className="sync-complete-label">No differences</div>
                  <div className="sync-complete-stats">
                    Configuration matches in this direction.
                  </div>
                </div>
              </div>
            )}

            {diff && !diff.fatalError && totalApplicable > 0 && (
              <>
                <p className="stui-step-desc">
                  <strong>{totalApplicable}</strong> row(s) can be applied.{' '}
                  <strong>{selectedRows.length}</strong> selected.
                  {selectedRows.length === 0 && ' Nothing is selected by default.'}
                  {totalBlockedRows > 0 && (
                    <> {totalBlockedRows} more differ but reference something the target
                    doesn&apos;t have.</>
                  )}
                </p>

                <div className="acs-table-list">
                  {tablesWithWork.map(t => {
                    const actionable = t.rows.filter(r => r.verdict !== 'TARGET_ONLY');
                    const targetOnly = t.rows.filter(r => r.verdict === 'TARGET_ONLY');
                    const open = !!expandedTables[t.table];
                    const chosen = perTableCounts[t.table] || 0;

                    return (
                      <div key={t.table} className="acs-table-block">
                        <button
                          className="acs-table-head"
                          onClick={() => setExpandedTables(p => ({ ...p, [t.table]: !open }))}
                        >
                          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <span className="acs-table-name">{t.table}</span>
                          <span className="acs-table-counts">
                            {actionable.length} to apply
                            {chosen > 0 && <strong> &middot; {chosen} selected</strong>}
                            {targetOnly.length > 0 && ` · ${targetOnly.length} target-only`}
                          </span>
                        </button>

                        {open && (
                          <div className="acs-table-body">
                            <div className="acs-key-note">
                              matched on <code>{t.keyColumns.join(' + ')}</code>
                            </div>

                            {actionable.length > 0 && (
                              <div className="acs-bulk-row">
                                <button className="acs-bulk-btn" onClick={() => setTableSelection(t, true)}>
                                  Select all {actionable.length}
                                </button>
                                <button className="acs-bulk-btn" onClick={() => setTableSelection(t, false)}>
                                  Clear
                                </button>
                              </div>
                            )}

                            {actionable.map(r => {
                              const tok = tokenOf(t.table, r.key);
                              const rowOpen = !!expandedRows[tok];
                              const blocked = !!r.blockedReason;
                              const hasSensitive = r.fields?.some(f => f.sensitive);
                              return (
                                <div key={tok} className={`acs-row${blocked ? ' acs-row-blocked' : ''}`}>
                                  <label className="acs-row-head">
                                    <input
                                      type="checkbox"
                                      checked={!blocked && selected.has(tok)}
                                      onChange={() => !blocked && toggleRow(t.table, r.key)}
                                      disabled={applying || blocked}
                                      title={blocked ? r.blockedReason : undefined}
                                    />
                                    <span className={`acs-verdict acs-verdict-${r.verdict.toLowerCase()}`}>
                                      {r.verdict}
                                    </span>
                                    <span className="acs-row-key">{r.key}</span>
                                    {hasSensitive && (
                                      <span className="acs-sensitive-tag" title="Changes a permission or access field">
                                        <ShieldAlert size={11} /> privilege
                                      </span>
                                    )}
                                    <span className="acs-row-count">{r.changeCount} change(s)</span>
                                  </label>

                                  {blocked && (
                                    <div className="acs-blocked-note">
                                      <Ban size={11} /> cannot apply — {r.blockedReason}
                                    </div>
                                  )}

                                  <button
                                    className="acs-row-expand"
                                    onClick={() => setExpandedRows(p => ({ ...p, [tok]: !rowOpen }))}
                                  >
                                    {rowOpen ? 'hide detail' : 'show detail'}
                                  </button>

                                  {rowOpen && (
                                    <div className="acs-row-detail">
                                      <table className="sync-history-table">
                                        <thead>
                                          <tr><th>Path</th><th>Source</th><th>Target</th></tr>
                                        </thead>
                                        <tbody>
                                          {r.fields.slice(0, 100).map((f, i) => (
                                            <tr key={i} className={
                                              f.sensitive ? 'acs-sensitive-row'
                                                : f.lowConfidence ? 'acs-lowconf' : ''
                                            }>
                                              <td>
                                                <code>{f.path}</code>
                                                {f.sensitive && (
                                                  <span className="acs-sensitive-mark" title="Permission or access field - read this one carefully.">!</span>
                                                )}
                                                {f.lowConfidence && (
                                                  <span className="acs-lowconf-tag" title="Element identity could not be established; this entry was matched by position and may be inexact.">?</span>
                                                )}
                                              </td>
                                              <td>{f.source ?? <em>(none)</em>}</td>
                                              <td>{f.target ?? <em>(none)</em>}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      {r.fields.length > 100 && (
                                        <p className="acs-more">
                                          +{r.fields.length - 100} more not shown
                                        </p>
                                      )}
                                      {r.sql && (
                                        <pre className="acs-sql">{r.sql}</pre>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {targetOnly.length > 0 && (
                              <div className="acs-targetonly">
                                <div className="acs-targetonly-head">
                                  <Ban size={12} />
                                  <span>
                                    Only in {diff.targetDatabase} — not actionable, never deleted
                                  </span>
                                </div>
                                {targetOnly.map(r => (
                                  <div key={r.key} className="acs-targetonly-item">{r.key}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Tables barred in this direction. Listed rather than hidden — a table silently
                missing from the list is indistinguishable from a table with no differences. */}
            {diff && !diff.fatalError && blockedTables.length > 0 && (
              <div className="acs-dirblock">
                <div className="acs-dirblock-head">
                  <Ban size={13} />
                  <span>Not available in this direction</span>
                </div>
                {blockedTables.map(t => (
                  <div key={t.table} className="acs-dirblock-item">
                    <strong>{t.table}</strong>
                    <span>{t.blockedReason}</span>
                  </div>
                ))}
                <p className="acs-dirblock-foot">
                  Switch to <strong>Prod &rarr; PreProd</strong> to review these.
                </p>
              </div>
            )}
          </div>

          {/* ═══════════ 3: APPLY ═══════════ */}
          <div className="stui-step-panel sync-validation-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">3</span>
              <h3>Apply</h3>
            </div>

            {applyResult && (
              <div className={`sync-complete-brief ${applyResult.status === 'SUCCESS' ? 'success' : 'failed'}`}>
                {applyResult.status === 'SUCCESS'
                  ? <CheckCircle size={18} className="icon-success" />
                  : <XCircle size={18} className="icon-fail" />}
                <div>
                  <div className="sync-complete-label">
                    {applyResult.status === 'SUCCESS' ? 'Applied' : applyResult.status}
                  </div>
                  <div className="sync-complete-stats">
                    {applyResult.errorMessage
                      ? applyResult.errorMessage
                      : `${applyResult.applied} applied, ${applyResult.skipped} skipped, ${applyResult.failed} failed`}
                  </div>
                </div>
              </div>
            )}

            {applyResult?.results?.length > 0 && (
              <>
                <div className="sync-history-table-wrap" style={{ marginTop: '10px' }}>
                  <table className="sync-history-table">
                    <thead>
                      <tr><th>Table</th><th>Key</th><th className="center">Action</th></tr>
                    </thead>
                    <tbody>
                      {applyResult.results.map((r, i) => (
                        <tr key={i}>
                          <td>{r.table}</td>
                          <td title={r.note || r.error || ''}>{r.key}</td>
                          <td className="center">
                            {r.success
                              ? <CheckCircle size={14} className="icon-success" />
                              : <span className="sync-res-warn">{r.action}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="sync-acknowledge-btn" onClick={() => setApplyResult(null)}>
                  <RotateCcw size={16} />
                  Acknowledge &amp; Reset
                </button>
              </>
            )}

            {!applyResult && selectedRows.length === 0 && (
              <p className="stui-step-desc">
                Select rows in step 2. Nothing is selected by default, and nothing is applied
                automatically.
              </p>
            )}

            {!applyResult && selectedRows.length > 0 && diff && (
              <>
                <div className="acs-apply-summary">
                  <div className="acs-apply-target">
                    Writing <strong>{selectedRows.length}</strong> row(s) to{' '}
                    <code className={isProdTarget ? 'acs-db-prod' : ''}>{diff.targetDatabase}</code>
                  </div>
                  <ul className="acs-apply-breakdown">
                    {Object.entries(perTableCounts).map(([tbl, n]) => (
                      <li key={tbl}>{tbl}: {n}</li>
                    ))}
                  </ul>
                </div>

                <label className="acs-ack">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={e => setAcknowledged(e.target.checked)}
                    disabled={applying}
                  />
                  <span>
                    I understand this overwrites live configuration in{' '}
                    <strong>{diff.targetDatabase}</strong> and may break the application.
                  </span>
                </label>

                <button className="import-execute-btn" disabled={!canApply} onClick={runApply}>
                  {applying ? <Loader size={16} className="spinning" /> : <Play size={16} />}
                  {applying
                    ? `Applying to ${diff.targetDatabase}...`
                    : `Apply ${selectedRows.length} row(s) to ${diff.targetDatabase}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default AdminConfigSyncPage;
